import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { SKIP_AUTH } from '@one/utils/http-context.utils';
import { EXPECTED_ORDER_NOT_FOUND } from '@shared/utils/expected-order-not-found.context';
import { LogErrorService } from '@shared/services/log-error.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import packageJson from '../../../../package.json';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';

const AUTH_ENDPOINTS_WITHOUT_REFRESH_RETRY = ['/auth/refresh-token', '/auth/signin-magiclink'];
const EXPECTED_REQUEST_ERRORS = [
  { status: 404, path: '/orders/me/last-finished' },
  { status: 404, path: '/my-packages' },
  { status: 404, path: '/order-reminders/' },
  { status: 409, path: '/order-reminders/' },
  { status: 410, path: '/order-reminders/' }
];

const shouldSkipRefreshRetry = (url: string): boolean => AUTH_ENDPOINTS_WITHOUT_REFRESH_RETRY.some((path) => url.includes(path));
const shouldSkipRequestLog = (error: any, req: HttpRequest<unknown>): boolean =>
  (error?.status === 404 && req.context.get(EXPECTED_ORDER_NOT_FOUND)) ||
  EXPECTED_REQUEST_ERRORS.some(({ status, path }) => error?.status === status && req.url.includes(path));

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const logErrorService = inject(LogErrorService);

  if (req.context.get(SKIP_AUTH)) return next(req);

  let newReq: HttpRequest<unknown> = req.clone();

  // Request
  //
  // Se o access token não estiver expirado, adicionaremos o header de autorização.
  // Não adicionaremos esse header para um token expirado.
  // Dessa forma forçamos o servidor a responder com um 401 mais rápido se necessário
  // e o interceptor tentará renovar o access token.
  if (authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken)) {
    newReq = req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken)
    });
  }

  return next(newReq).pipe(
    catchError((error: any) => {
      // Se o erro for um erro de autorização
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Endpoints de auth sem retry e sessão sem refresh token: apenas propagamos o erro.
        // O signOut (quando cabível) é responsabilidade do AuthService, e só em 401 real.
        if (shouldSkipRefreshRetry(req.url) || !authService.refreshToken) return throwError(() => error);

        // Tentar obter um novo access token com o refresh token e reenviar a requisição original
        return authService.signInRefreshToken().pipe(
          switchMap((newToken: string) => {
            const retryReq = req.clone({
              headers: req.headers.set('Authorization', 'Bearer ' + newToken),
              // O sign-in-with-token valida o access token enviado no body.
              body: req.url.includes('/auth/sign-in-with-token') ? { accessToken: newToken } : req.body
            });

            // Um 401 da rota após refresh bem-sucedido não prova que o refresh token é inválido.
            return next(retryReq);
          })
        );
      } else {
        const shouldSkipLog =
          (error.status === 0 && navigator?.onLine === false) ||
          (error.status === 400 && newReq?.url?.includes('/auth/signin-magiclink')) ||
          (error.status === 400 && newReq?.url?.includes('/auth/refresh-token')) ||
          error.status === 401 ||
          shouldSkipRequestLog(error, newReq);

        if (shouldSkipLog) {
          return throwError(() => error);
        }

        if (error.status === 0) {
          const errorDetails = {
            status: error?.status,
            statusText: error?.statusText,
            url: error?.url || newReq?.url,
            method: newReq?.method,
            body: newReq?.body,
            headers: Object.fromEntries(newReq?.headers?.keys()?.map((key) => [key, newReq?.headers.get(key)]) || []),
            errorName: error?.name,
            errorMessage: error?.message,
            timestamp: new Date().toISOString(),
            userAgent: navigator?.userAgent,
            online: navigator?.onLine,
            connectionType: (navigator as any)?.connection?.effectiveType || 'unknown'
          };

          logErrorService.warning(`(v${packageJson.version}) - ${error.status}: Erro na requisição ${newReq?.url}`, errorDetails);
        } else {
          logErrorService.warning(`(v${packageJson.version}) - ${error.status}: Erro na requisição ${newReq?.url}`, {
            status: error.status,
            body: newReq?.body,
            ...(error.status < 500 ? { response: error?.error } : {}),
            url: newReq?.url,
            method: newReq?.method
          });
        }
      }

      return throwError(() => error);
    })
  );
};
